import { useTranslate } from '../../application/i18n/LocaleProvider';

/**
 * The answer, ABOVE THE PAIR, in the slot the question was asked in
 * (SPEC-007 D-66).
 *
 * It used to be the centrepiece of a plate under the two cards, and the cost of
 * that was the whole shape of the screen: the answer needed two hundred pixels
 * nobody had, so the pair collapsed to a letterbox to pay for them and the round
 * ended on a layout the reader had not been playing. The two stages of a round
 * are now the SAME COMPOSITION — what is being compared, then the two cards,
 * then the clock — and only the line between them changes: an instruction while
 * the question is open, the two figures once it is answered.
 *
 * WHAT IT SHOWS IS THE FIGURES AND THE DISTANCE BETWEEN THEM, and nothing else.
 * The attribute is already drawn as a plate directly above this — it is the
 * question's own subject, and it stays lit through the answer — so a title here
 * would be the word twice, one line apart. The car NAMES went the same way and
 * for a better reason: each figure is drawn over the card it belongs to, in the
 * same two columns the pair is laid out in, so the picture underneath is the
 * label. That is what buys the size the numbers are set at.
 *
 * THE TWO FIGURES ARE THE HEADLINE AND THE BAR EXPLAINS THEM (D-64). They are
 * set as ONE CENTRED OBJECT — "12 vs 6" — rather than as two numbers held over
 * their own cards, and that is a trade rather than a tidy-up (D-67). Column
 * alignment meant the picture underneath could do the labelling; centring
 * spends that and buys the size, and what pays the difference is ORDER: the left
 * figure is the left car because there are exactly two of them and nothing else
 * on the screen is laid out any other way. The bar under them stops being the
 * thing to decode and becomes the thing that says by how much.
 *
 * LENGTH IS THE QUANTITY, COLOUR IS THE ANSWER (D-12). One track split between
 * the two cars, each taking its share of their total, and the winner's piece
 * takes the heat ramp however long it is — because the larger piece is not
 * always the winner's: on "which one is lighter" the heavier car takes more of
 * the bar and loses, and on "which one is rarer" the winner's share is a sliver.
 * That separation is what lets one drawing serve all eleven questions honestly.
 * The hairline at the centre is the "even" the split is read against.
 *
 * The split grows out of the centre under `--duration-reveal`, so the two cars
 * push against each other and settle where the figures put them. Under reduced
 * motion the tokens collapse and it simply arrives split.
 *
 * A DRAWING, so it is `aria-hidden` — the same split `DuelQuestion` makes. It
 * sits outside the reveal's live region now that it is above the pair, and a
 * band that announced itself from up here would talk over the outcome. The
 * figures are carried as text inside the reveal instead, in the order a reader
 * asks for them: what happened, then what the numbers were.
 */
export default function DuelGauge({ insight }) {
    const t = useTranslate();
    const [left] = insight.comparison;
    /**
     * How much room the two figures need, in the only unit CSS cannot ask for:
     * characters (D-67).
     *
     * "12 vs 6" and "4,943 vs 3,366" are the same row and not the same problem.
     * The first can be set at four times the label beside it; the second cannot
     * be set at half that without running off a 375px phone — and a figure that
     * clips is not a figure, which is the one thing this band may never do. A
     * viewport clamp cannot tell them apart, because it does not know what is in
     * them. So the component measures its own content and the sheet takes the
     * step down, which keeps the common case — a cylinder count, a lap time, a
     * quarter mile — as big as it can honestly be.
     */
    const isWide = insight.comparison.some((entry) => String(entry.value).length > 3);

    return (
        <div className={`duel-gauge${isWide ? ' duel-gauge--wide' : ''}`} aria-hidden="true">
            {/* THE HEAD TO HEAD, and the thing the reader is actually here for.

                The VS is IN FLOW between the two figures rather than drawn over
                the gap they leave. Over the gap is right when the two are equal
                columns, which is what this was when each figure sat over its own
                card; centred as one object they are content-width and rarely
                equal, and the midpoint of the gap then sits `(left - right) / 2`
                away from the midpoint of the row — so a mark pinned to the row's
                centre lands on the wider number. On a 5-against-3 digit pairing
                that was a real overlap, not a rounding one. In flow it is between
                them by construction, at every width, on every engine. */}
            <div className="duel-gauge__ends">
                {duelValue(insight.comparison[0], 0)}

                <span className="duel-gauge__vs">{t('duel.versus')}</span>

                {duelValue(insight.comparison[1], 1)}
            </div>

            {/* BY HOW MUCH, centred under the two figures and above the bar
                (D-67). It carries the unit inside it — "170 hp apart" — which is
                why there is no separate unit caption here: the row said the same
                word twice for a revision, once under each number and once in
                this line, and one of them had to go.

                It was beside the bar on one line for a revision before that, on
                the argument that a bar is a needle and the figure beside it is
                the dial. Centred above it the whole band reads down one axis —
                the two numbers, what separates them, then the drawing of that —
                and the bar gets the full width to say it in. */}
            <p className="duel-gauge__margin">
                {t('duel.gap', {
                    margin: insight.margin,
                    unit: insight.hasUnit ? t(insight.unitKey) : '',
                })}
            </p>

            <span className="duel-gauge__bar" style={{ '--duel-split': left.share }}>
                <span className={sideClass(left, 'left')} />
                <span className={sideClass(insight.comparison[1], 'right')} />
                {/* Even. The thing the split is read against. */}
                <span className="duel-gauge__even" />
            </span>
        </div>
    );
}

/** One figure, drawn as the side of the pair it belongs to. */
function duelValue(entry, index) {
    return (
        <p className={valueClass(entry, index)} key={entry.carId}>
            <span className="figure">{entry.value}</span>
        </p>
    );
}

/** Which side of the pair this figure belongs to, and whether it won. */
function valueClass(entry, index) {
    return [
        'duel-gauge__value',
        index === 0 ? 'duel-gauge__value--left' : 'duel-gauge__value--right',
        entry.isWinner ? 'duel-gauge__value--winner' : '',
    ].filter(Boolean).join(' ');
}

function sideClass(entry, side) {
    return [
        'duel-gauge__side',
        `duel-gauge__side--${side}`,
        entry.isWinner ? 'duel-gauge__side--winner' : '',
    ].filter(Boolean).join(' ');
}
